<template lang="pug">
  .main
    PDFModal(ref="pdfModal", :class_options="[]", :config="config", :detail_list="currentDetailList")
    base-header.pb-1
      b-row.py-4(align-v='center')
        b-col(cols='7' lg='6')
          h6.h2.text-white.d-inline-block.mb-0 LLM Benchmark

    b-container.mt-4(fluid)
      //- Full-width: new run with a wide, readable project table
      b-card.mb-4
        .d-flex.justify-content-between.align-items-center.mb-3
          h5.mb-0 Neuer Run
          b-button(variant="link", size="sm", @click="fetchCandidates", :disabled="loadingCandidates") Aktualisieren
        p.small.text-muted Wähle die Projekte, die du auswerten willst. Es wird davon ausgegangen, dass die Projekte bereits analysiert wurden und Gold-Daten existieren.

        .text-muted.small.py-3(v-if="loadingCandidates") Lade Projekte…
        template(v-else)
          .table-responsive.benchmark-candidate-table
            table.table.table-hover.align-middle.mb-0
              thead
                tr
                  th(style="width: 40px")
                    b-form-checkbox(
                      :checked="allReadySelected",
                      @change="toggleSelectAllReady",
                      :disabled="!readyCandidates.length"
                    )
                  th Projekt
                  th.text-right Gold
                  th.text-right LLM
                  th.text-right Geprüft
                  th Status
                  th.text-right Aktion
              tbody
                tr(
                  v-for="c in candidates",
                  :key="c.project_id",
                  :class="{ 'text-muted': !c.ready }"
                )
                  td
                    b-form-checkbox(
                      v-model="selectedIds",
                      :value="c.project_id",
                      :disabled="!c.ready"
                    )
                  td
                    span.font-weight-600 {{ c.name }}
                  td.text-right {{ c.gold_documents }}
                  td.text-right {{ c.llm_documents }}
                  td.text-right {{ c.reviewed_documents }}
                  td
                    b-badge(v-if="c.ready", variant="success") bereit
                    b-badge(v-else, variant="light") nicht bereit
                    b-badge.ml-1(v-if="c.gold_outdated > 0", variant="warning") Gold veraltet ({{ c.gold_outdated }})
                  td.text-right
                    b-button(
                      v-if="needsGold(c)",
                      variant="outline-secondary",
                      size="sm",
                      @click="copyGold(c)",
                      :disabled="copyingId === c.project_id"
                    )
                      span(v-if="copyingId === c.project_id") …
                      span(v-else) Gold übernehmen

          .d-flex.justify-content-between.align-items-center.mt-3
            span.small.text-muted {{ selectedIds.length }} ausgewählt
            b-button(
              variant="primary",
              @click="startRun",
              :disabled="!selectedIds.length || running"
            )
              span(v-if="running") Läuft…
              span(v-else) Benchmark starten ({{ selectedIds.length }})

      b-row
        //- Left column: runs history
        b-col(cols='12' lg='4')
          b-card
            h5.mb-3 Vergangene Runs
            .text-muted.small(v-if="!runs.length") Noch keine Runs vorhanden.
            .list-group(v-else)
              a.list-group-item.list-group-item-action(
                v-for="r in runs",
                :key="r._id",
                :class="{ active: selectedRun && selectedRun._id === r._id }",
                @click.prevent="selectRun(r._id)"
              )
                .d-flex.justify-content-between.align-items-center
                  span.small {{ formatDate(r.created_at) }}
                  b-badge(variant="info") {{ r.documents_evaluated }} Dok.
                .small.text-truncate {{ (r.project_names || []).join(', ') }}
                .small.text-muted
                  span F1 {{ pct(r.boundary_f1) }}
                  span.ml-2 DCC {{ pct(r.dcc_accuracy) }}
                  span.ml-2 {{ money(r.cost_total) }}

        //- Right column: selected run detail
        b-col(cols='12' lg='8')
          .text-center.text-muted.py-5(v-if="!selectedRun") Wähle oder starte einen Run, um die Ergebnisse zu sehen.

          template(v-else)
            //- KPI cards (all documents)
            h6.mb-2 Gesamt
            b-row
              b-col(cols='6' md='3' v-for="kpi in overallKpis", :key="'all-' + kpi.label")
                b-card.kpi-card.mb-3
                  .small.text-muted {{ kpi.label }}
                  .h3.mb-0 {{ kpi.value }}
                  .small.text-muted(v-if="kpi.sub") {{ kpi.sub }}

            //- KPI cards (documents < 10 pages)
            h6.mb-2.mt-2 Dokumente &lt; 10 Seiten
              b-badge.ml-2(variant="light") {{ shortDocsKpis.length ? shortDocsStats.documents_evaluated + ' Dok.' : '—' }}
            b-row(v-if="shortDocsKpis.length")
              b-col(cols='6' md='3' v-for="kpi in shortDocsKpis", :key="'short-' + kpi.label")
                b-card.kpi-card.mb-3.kpi-card-short
                  .small.text-muted {{ kpi.label }}
                  .h3.mb-0 {{ kpi.value }}
                  .small.text-muted(v-if="kpi.sub") {{ kpi.sub }}
            .text-muted.small.mb-3(v-else) Keine ausgewerteten Dokumente mit weniger als 10 Seiten.

            b-card.mb-4
              h6.mb-3 KPIs pro Projekt
              .table-responsive.benchmark-result-table
                table.table.table-striped.table-hover.align-middle.mb-0
                  thead
                    tr
                      th Projekt
                      th.text-right Dok.
                      th.text-right Seiten
                      th.text-right F1
                      th.text-right DCC
                      th.text-right Match
                      th.text-right Kosten
                      th.text-right $/Dok
                      th.text-right $/Seite
                      th.text-right Zeit
                      th.text-right s/Dok
                      th.text-right s/Seite
                  tbody
                    tr(v-for="p in selectedRun.per_project", :key="p.project_id")
                      td.small {{ p.project_name || p.project_id }}
                      td.text-right.small {{ p.documents_evaluated }}/{{ p.documents_total }}
                      td.text-right.small {{ p.pages_total }}
                      td.text-right.small {{ pct(p.boundary_f1) }}
                      td.text-right.small {{ pct(p.dcc_accuracy) }}
                      td.text-right.small {{ pct(p.split_count_match_rate) }}
                      td.text-right.small {{ money(p.cost_total) }}
                      td.text-right.small {{ money(p.cost_per_doc) }}
                      td.text-right.small {{ money(p.cost_per_page) }}
                      td.text-right.small {{ secs(p.time_s_total) }}
                      td.text-right.small {{ secs(p.time_per_doc) }}
                      td.text-right.small {{ secs(p.time_per_page) }}

      //- Full-width: per-file comparisons
      template(v-if="selectedRun")
        b-card.mt-2
          .d-flex.justify-content-between.align-items-center.mb-3
            h6.mb-0 Vergleiche pro Datei
            b-form-checkbox(v-model="onlyFailures") Nur Probleme
          .table-responsive.benchmark-result-table
            table.table.table-striped.table-hover.align-middle.mb-0
              thead
                tr
                  th Projekt
                  th Datei
                  th.text-right Seiten
                  th Status
                  th.text-right F1
                  th.text-right DCC
                  th.text-right Splits (G/L)
                  th.text-right Kosten
                  th.text-right Zeit
                  th
              tbody
                tr(v-for="d in visibleDocuments", :key="d.document_id")
                  td.small {{ d.project_name }}
                  td.small
                    b-button.p-0.text-left(variant="link", size="sm", @click="openPdfModal(d)", :title="d.filename") {{ d.filename }}
                  td.text-right.small {{ d.pages }}
                  td.small
                    b-badge(v-if="d.evaluated", :variant="d.split_count_match ? 'success' : 'warning'") {{ d.split_count_match ? 'Match' : 'Abw.' }}
                    b-badge(v-else, variant="danger") {{ d.reason || 'n/a' }}
                  td.text-right.small {{ d.evaluated ? pct(d.boundary_f1) : '—' }}
                  td.text-right.small {{ d.evaluated ? pct(d.dcc_accuracy) : '—' }}
                  td.text-right.small {{ d.gold_splits }} / {{ d.llm_splits }}
                  td.text-right.small {{ money(d.cost) }}
                  td.text-right.small {{ secs(d.time_s) }}
                  td.text-right
                    b-button(variant="link", size="sm", @click="openDiff(d)") Diff

    b-modal(v-model="diffModal", size="lg", :title="diffTitle", ok-only, ok-title="Schließen")
      .text-muted.small(v-if="diffLoading") Lade…
      template(v-else-if="diffData")
        b-row
          b-col(cols='6')
            h6 Gold
            pre.benchmark-diff {{ formatAssignments(diffData.gold_assignments) }}
          b-col(cols='6')
            h6 LLM
            pre.benchmark-diff {{ formatAssignments(diffData.llm_assignments) }}
        h6.mt-3 LLM Run Metadaten
        pre.benchmark-diff {{ JSON.stringify(diffData.llm_run_metadata || {}, null, 2) }}
</template>

<script>
import PDFModal from '@/views/Dashboard/PDFModal.vue';

export default {
  components: {
    PDFModal,
  },
  data() {
    return {
      config: {},
      currentDetailList: { entries: [], all_classes: [] },
      candidates: [],
      loadingCandidates: false,
      selectedIds: [],
      running: false,
      copyingId: null,
      runs: [],
      selectedRun: null,
      onlyFailures: false,
      diffModal: false,
      diffLoading: false,
      diffData: null,
      diffTitle: '',
    };
  },
  computed: {
    readyCandidates() {
      return this.candidates.filter(c => c.ready);
    },
    allReadySelected() {
      return (
        this.readyCandidates.length > 0 &&
        this.readyCandidates.every(c => this.selectedIds.includes(c.project_id))
      );
    },
    shortDocsStats() {
      const r = this.selectedRun;
      if (!r) return null;
      if (r.short_docs) return r.short_docs;
      return this.computeShortDocsStats(r.documents || []);
    },
    overallKpis() {
      const r = this.selectedRun;
      if (!r) return [];
      return this.buildKpiCards(r);
    },
    shortDocsKpis() {
      const stats = this.shortDocsStats;
      if (!stats || !stats.documents_evaluated) return [];
      return this.buildKpiCards(stats, { isSubset: true });
    },
    visibleDocuments() {
      const docs = (this.selectedRun && this.selectedRun.documents) || [];
      if (this.onlyFailures) {
        return docs.filter(d => !d.evaluated || !d.split_count_match);
      }
      return docs;
    },
  },
  methods: {
    buildKpiCards(stats, { isSubset = false } = {}) {
      if (!stats) return [];
      const docsLabel = isSubset
        ? stats.documents_evaluated + ' Dok.'
        : stats.documents_evaluated + '/' + (stats.documents_total || stats.documents_evaluated);
      return [
        { label: 'Boundary F1', value: this.pct(stats.boundary_f1), sub: 'Split-Genauigkeit' },
        { label: 'DCC Accuracy', value: this.pct(stats.dcc_accuracy), sub: 'Klassifizierung' },
        { label: 'Split-Match', value: this.pct(stats.split_count_match_rate), sub: 'gleiche Anzahl Splits' },
        { label: 'Dokumente', value: docsLabel, sub: stats.pages_total + ' Seiten' },
        {
          label: 'Kosten gesamt',
          value: this.money(stats.cost_total),
          sub: (stats.input_tokens_total + stats.output_tokens_total) + ' Tokens',
        },
        { label: 'Kosten / Dok', value: this.money(stats.cost_per_doc), sub: 'pro Dokument' },
        { label: 'Kosten / Seite', value: this.money(stats.cost_per_page), sub: 'pro Seite' },
        { label: 'Zeit gesamt', value: this.secs(stats.time_s_total), sub: this.secs(stats.time_per_doc) + ' / Dok' },
        { label: 'Zeit / Seite', value: this.secs(stats.time_per_page), sub: '' },
      ];
    },
    computeShortDocsStats(documents) {
      const evaluated = (documents || []).filter(d => d.evaluated && d.pages < 10);
      if (!evaluated.length) {
        return { documents_evaluated: 0, pages_total: 0 };
      }
      const sum = (arr, key) => arr.reduce((acc, d) => acc + (d[key] || 0), 0);
      const avg = (arr, key) => sum(arr, key) / arr.length;
      const pagesTotal = sum(evaluated, 'pages');
      const costTotal = sum(evaluated, 'cost');
      const timeTotal = sum(evaluated, 'time_s');
      const splitMatch = evaluated.filter(d => d.split_count_match).length;
      return {
        documents_evaluated: evaluated.length,
        pages_total: pagesTotal,
        boundary_f1: avg(evaluated, 'boundary_f1'),
        dcc_accuracy: avg(evaluated, 'dcc_accuracy'),
        split_count_match_rate: splitMatch / evaluated.length,
        cost_total: costTotal,
        cost_per_doc: costTotal / evaluated.length,
        cost_per_page: pagesTotal ? costTotal / pagesTotal : 0,
        time_s_total: timeTotal,
        time_per_doc: timeTotal / evaluated.length,
        time_per_page: pagesTotal ? timeTotal / pagesTotal : 0,
        input_tokens_total: sum(evaluated, 'input_tokens'),
        output_tokens_total: sum(evaluated, 'output_tokens'),
      };
    },
    pct(v) {
      if (v == null) return '—';
      return (v * 100).toFixed(1) + '%';
    },
    money(v) {
      if (v == null) return '—';
      return Number(v).toFixed(4) + ' €';
    },
    secs(v) {
      if (v == null) return '—';
      return Number(v).toFixed(1) + 's';
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('de-DE');
    },
    formatAssignments(list) {
      if (!list || !list.length) return '—';
      return list
        .map(a => {
          const cls = (a.selected_class || []).join(', ') || '—';
          return `S. ${a.start}-${a.end}  [${cls}]`;
        })
        .join('\n');
    },
    toggleSelectAllReady(checked) {
      if (checked) {
        const ids = new Set(this.selectedIds);
        this.readyCandidates.forEach(c => ids.add(c.project_id));
        this.selectedIds = Array.from(ids);
      } else {
        const readyIds = new Set(this.readyCandidates.map(c => c.project_id));
        this.selectedIds = this.selectedIds.filter(id => !readyIds.has(id));
      }
    },
    async fetchCandidates() {
      this.loadingCandidates = true;
      try {
        const res = await this.axios.get(this.base_url + '/benchmark/candidates');
        this.candidates = res.data || [];
      } catch (err) {
        this.toastError(err, 'Projekte konnten nicht geladen werden');
      } finally {
        this.loadingCandidates = false;
      }
    },
    needsGold(c) {
      return c.gold_missing > 0 || c.gold_outdated > 0;
    },
    async copyGold(c) {
      const msg =
        'Gold-Daten für "' + c.name + '" aus den aktuell geprüften Dokumenten übernehmen?\n\n' +
        'Es wird der aktuelle Stand der Zuordnungen (assignments) aller geprüften Dokumente als Gold gespeichert ' +
        'und vorhandenes, abweichendes Gold überschrieben. ' +
        'Das sollte VOR einem LLM-Lauf geschehen, solange die Zuordnungen noch das geprüfte (menschliche) Ergebnis sind.';
      const ok = await this.$bvModal.msgBoxConfirm(msg, {
        title: 'Gold übernehmen',
        okTitle: 'Übernehmen',
        cancelTitle: 'Abbrechen',
      });
      if (!ok) return;
      this.copyingId = c.project_id;
      try {
        const res = await this.axios.post(
          this.base_url + '/benchmark/' + c.project_id + '/refresh-gold'
        );
        const n = (res.data && res.data.refreshed) || 0;
        this.$bvToast.toast(n + ' Dokument(e) als Gold übernommen', {
          title: 'Gold übernommen',
          variant: 'primary',
          autoHideDelay: 2500,
        });
        await this.fetchCandidates();
      } catch (err) {
        this.toastError(err, 'Gold-Übernahme fehlgeschlagen');
      } finally {
        this.copyingId = null;
      }
    },
    async fetchRuns() {
      try {
        const res = await this.axios.get(this.base_url + '/benchmark/runs');
        this.runs = res.data || [];
      } catch (err) {
        this.toastError(err, 'Runs konnten nicht geladen werden');
      }
    },
    async selectRun(runId) {
      try {
        const res = await this.axios.get(this.base_url + '/benchmark/runs/' + runId);
        this.selectedRun = res.data;
      } catch (err) {
        this.toastError(err, 'Run konnte nicht geladen werden');
      }
    },
    async startRun() {
      if (!this.selectedIds.length) return;
      this.running = true;
      try {
        const res = await this.axios.post(this.base_url + '/benchmark/runs', {
          project_ids: this.selectedIds,
        });
        this.selectedRun = res.data;
        await this.fetchRuns();
        this.$bvToast.toast('Benchmark abgeschlossen', {
          title: 'Fertig',
          variant: 'primary',
          autoHideDelay: 2500,
        });
      } catch (err) {
        this.toastError(err, 'Benchmark fehlgeschlagen');
      } finally {
        this.running = false;
      }
    },
    async openPdfModal(doc) {
      if (!doc || !doc.project_id || !doc.document_id) return;
      try {
        const res = await this.axios.get(
          this.base_url + '/projects/' + doc.project_id + '/detail_list'
        );
        this.currentDetailList = res.data || { entries: [], all_classes: [] };
      } catch (err) {
        this.currentDetailList = { entries: [], all_classes: [] };
      }
      this.$refs.pdfModal.open({
        project_id: doc.project_id,
        _id: doc.document_id,
      });
    },
    async openDiff(doc) {
      this.diffModal = true;
      this.diffLoading = true;
      this.diffData = null;
      this.diffTitle = doc.filename || 'Diff';
      try {
        const res = await this.axios.get(
          this.base_url + '/benchmark/' + doc.project_id + '/diff/' + doc.document_id
        );
        this.diffData = res.data;
      } catch (err) {
        this.toastError(err, 'Diff konnte nicht geladen werden');
        this.diffModal = false;
      } finally {
        this.diffLoading = false;
      }
    },
    toastError(err, fallback) {
      const detail = err && err.response && err.response.data && err.response.data.detail;
      this.$bvToast.toast(detail || fallback, {
        title: 'Fehler',
        variant: 'danger',
        autoHideDelay: 4000,
      });
    },
  },
  mounted() {
    this.fetchCandidates();
    this.fetchRuns();
  },
};
</script>

<style scoped>
.benchmark-candidate-table {
  max-height: 420px;
  overflow-y: auto;
}

.benchmark-candidate-table table th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8898aa;
  border-top: none;
}

.benchmark-candidate-table table td {
  font-size: 0.9rem;
  white-space: nowrap;
}

.font-weight-600 {
  font-weight: 600;
}

/* Larger, more readable result tables */
.benchmark-result-table table {
  font-size: 0.9rem;
}

.benchmark-result-table table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8898aa;
  white-space: nowrap;
}

.benchmark-result-table table td {
  white-space: nowrap;
}

/* Neutralise the tiny .small cells so result tables stay readable */
.benchmark-result-table table td.small,
.benchmark-result-table table .small {
  font-size: 0.9rem;
}

.kpi-card {
  text-align: center;
}

.kpi-card-short {
  border-left: 3px solid #5e72e4;
}

.benchmark-diff {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: 0.78rem;
  white-space: pre-wrap;
  max-height: 320px;
  overflow-y: auto;
}

.list-group-item.active {
  background-color: #003c50;
  border-color: #003c50;
}
</style>
